import { useState, useEffect, useRef } from 'react'
import { flushSync } from 'react-dom'
import { useNavigate, useLocation } from 'react-router-dom'
import { Check, Circle, Camera, Lock, Trash2, FileUp } from 'lucide-react'
import { useCaseStore, useProfileStore } from '../../stores'
import useQuestionnaireStore from '../../stores/useQuestionnaireStore'
import { useI18n } from '../../i18n'
import { isDescribeLocked } from '../../utils/describeLock'
import { useQuestionnaireTranslation } from '../../i18n/questionnaires/useQuestionnaireTranslation'
import DeleteCaseModal from '../../components/DeleteCaseModal'
import { isHeldByDoctor, reportDeleteFailure } from '../../utils/caseDelete'
import { ChatKeyframes, CompactHeader, OrellaChatBubble } from '../../components/OrellaChatHeader'
import OrellaThinkingBubble from '../../components/OrellaThinkingBubble'
import { aiAPI } from '../../services/api'
import { prefillFromProfile, getAllQuestionsForCase } from '../../utils/voicePrefill'
import { stripQuestionText, buildQuestionTextEntries, mergeQuestionText } from '../../utils/questionText'
import { mergeRehydratedAnswers } from '../../utils/describeRehydrate'
// s100 (Dr. Rami 2026-10-11): the two new question types and the urgent-answer cards.
import AnswerAlertCard from '../../components/describe/AnswerAlertCard'
import PackYearsInput from '../../components/describe/PackYearsInput'
import EpworthScale from '../../components/describe/EpworthScale'
import ScoredScale from '../../components/describe/ScoredScale'
import { alertIdFor, alertCard } from '../../utils/answerAlerts'
// catalog 3.7 (Dr. Rami 2026-10-11): show rules (sex / age / an earlier answer) and the 'number' type.
import NumberInput from '../../components/describe/NumberInput'
import BottomActionBar from '../../components/BottomActionBar'
import { patientFacts, isQuestionShown, visibleOptions, dropHiddenAnswers } from '../../utils/questionVisibility'
import { priorTestTriggered } from '../../utils/priorTests'
import { AUGMENT_CATEGORIES, startAugment } from '../../utils/augmentPrefetch'

// Color constants
const NAVY = '#15354F'             // Navy for icons/text

// v226.10.0 / PROD-1227: the augment calls, their `priorQuestions` (every augment question the
// patient has already seen, from the sessionStorage cache) and their single-flight registry live
// in utils/augmentPrefetch.js. One call per (case, category); every screen that needs it joins.

export default function DescribeSection() {
  const location = useLocation()
  const navigate = useNavigate()
  const { currentCase, updateCase, updateAndSaveCase, clearCurrentCase, deleteCase } = useCaseStore()
  const profile = useProfileStore((s) => s.profile)
  const [answers, setAnswers] = useState({})
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const { t, language } = useI18n()
  const { tq, to } = useQuestionnaireTranslation() // Translate questions and options
  const profilePrefillDoneRef = useRef(false)
  
  // Extract category from URL path (needed early for showContent init)
  const pathParts = location.pathname.split('/')
  const category = pathParts[pathParts.length - 1]

  // v213.9 (Sprint 13C-A2): General specialty AI augmentation.
  // For 'general' specialty cases only, after each anchor category is answered we
  // call POST /ai/augment-category to generate 1-3 AI follow-ups. The AI follow-ups
  // for the PRIOR category render appended at the bottom of the CURRENT screen as
  // a "few more" section. Categories: details → signs-symptoms → context → additional.
  // v229.0.0: per-category augmentation serves the General path AND the canonical-specialty
  // general-concern cards (focus area id ends in '-general'); both are specialty-primed server-side.
  const isGeneralSpecialty = (currentCase?.specialtyId || '').toLowerCase() === 'general'
    || (currentCase?.focusAreaId || '').toLowerCase().endsWith('-general')

  // Map URL-form category to underscore-form key used by API/storage
  const URL_TO_KEY = {
    'details': 'details',
    'signs-symptoms': 'signs_symptoms',
    'context': 'context',
    'additional': 'additional_factors',
  }
  const KEY_TO_URL = {
    'details': 'details',
    'signs_symptoms': 'signs-symptoms',
    'context': 'context',
    'additional_factors': 'additional',
  }

  // The previous category key — what augment to render appended on this screen.
  // null on the first category (details).
  const PREV_OF = {
    'details': null,
    'signs-symptoms': 'details',
    'context': 'signs_symptoms',
    'additional': 'context',
  }
  const prevCategoryKey = PREV_OF[category] || null

  // Augment questions (for the PRIOR category) shown appended on this screen.
  const [augmentQs, setAugmentQs] = useState([])
  const [augmentLoading, setAugmentLoading] = useState(false)
  const augmentFetchedForRef = useRef(null) // track which (case, prevCat) we've already fetched

  // s100: red urgent cards need "I understand" before the patient goes on. Acks are kept per case for the
  // session ({ answerKey: cardId }); a different (more severe) card on the same question needs a new tap.
  const alertAckStore = `orella_alert_acks_${currentCase?.id}`
  const [alertAcks, setAlertAcks] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(alertAckStore) || '{}') } catch (_) { return {} }
  })
  const ackAlert = (answerKey, cardId) => setAlertAcks((prev) => {
    const next = { ...prev, [answerKey]: cardId }
    try { sessionStorage.setItem(alertAckStore, JSON.stringify(next)) } catch (_) { /* session storage unavailable — acks stay in memory */ }
    return next
  })


  // Initialize showContent from sessionStorage — if animation already played, show immediately
  // This prevents the "invisible content on back-nav" bug caused by race condition
  // between framer-motion page transitions and the 60ms OrellaChatBubble callback
  const [showContent, setShowContent] = useState(() => {
    const key = `orella_chat_describe_${category}_${currentCase?.id}`
    return !!sessionStorage.getItem(key)
  })

  // v219.5.1 / v219.5.7: Survey-extraction loading gate.
  // After the voice memo, DescribeVoice.handleNext fires runPrefillFromText
  // FIRE-AND-FORGET, then navigates synchronously. runPrefillFromText sets
  // currentCase.surveyExtractionPending=true, but there is a brief window on
  // THIS screen's mount before that flag has propagated through the store —
  // during which the OLD gate (which required surveyExtractionPending===true)
  // was false, so the empty questionnaire + chat bubble flashed for a beat and
  // THEN the loader appeared. That flash IS the "few seconds where everything
  // loads" Dr. Rami reported.
  //
  // v219.5.7 fix: on the FIRST category ('details') of a SPECIALTY case with a
  // transcript, show the branded loader whenever extraction is NOT yet
  // known-complete — i.e. cover BOTH the pending state AND the pre-flag
  // window. "Known-complete" = the prefill landed (voicePrefilled set true by
  // applyPrefills on success) OR the pending flag has been set then cleared.
  // The 12s cap (was 10s) is the "unless ridiculous" escape so a stuck/absent
  // flag can never trap the patient — extraction continues in the background
  // and still fills in if it lands after reveal.
  //
  // SCOPE: specialty path ONLY. General is deliberately excluded — it has its
  // own augment-driven flow (the augment pre-fill + "A few more" section land
  // progressively and must not be gated behind this whole-screen loader).
  const hasTranscript = !!(
    (currentCase?.voiceTranscript && currentCase.voiceTranscript.trim()) ||
    (currentCase?.voiceTextInput && currentCase.voiceTextInput.trim())
  )
  // Extraction is resolved once runPrefillFromText has hit ANY terminal exit
  // (success, zero-result, no-case, error) — signalled by surveyExtractionDone.
  // This is unambiguous even when 0 answers landed (voicePrefilled would stay
  // false then and incorrectly keep the loader up to its cap).
  const extractionResolved = currentCase?.surveyExtractionDone === true
  const [extractionCapReached, setExtractionCapReached] = useState(false)
  // v219.5.8: General is NO LONGER excluded. v219.5.7 excluded it on the
  // theory its augment flow covered progressive loading, but testing showed
  // General runs anchor extraction exactly like specialty ("I've marked a few
  // responses already" proves it) — same post-voice-memo gap, same loader.
  const showExtractionLoader =
    category === 'details' &&
    hasTranscript &&
    !extractionResolved &&
    !extractionCapReached

  useEffect(() => {
    // Arm the 12s cap whenever the loader is (or could be) showing — including
    // the pre-flag window, so a flag that never sets can't trap the patient.
    if (category !== 'details') return
    if (!hasTranscript) return
    if (extractionResolved) return
    if (extractionCapReached) return
    const cap = setTimeout(() => {
      console.log('[DescribeSection] Extraction loader 30s cap reached — revealing questions; extraction continues in background')
      setExtractionCapReached(true)
    }, 30000)
    return () => clearTimeout(cap)
  }, [category, hasTranscript, extractionResolved, extractionCapReached])

  // v219.5.8 (Issue #1): For General, pre-fire the 'details' augment generation
  // as soon as the patient lands on the Details screen — i.e. DURING the
  // post-voice-memo extraction loader window — instead of waiting for
  // handleNext when they leave Details. The details-augment renders on the
  // NEXT screen (signs-symptoms), and the Sonnet call takes a few seconds;
  // firing it now means it's warm/cached by the time they get there. Reuses
  // the SAME sessionStorage in-flight + result guards as the handleNext
  // pre-warm so the two can never double-fire (whichever runs first wins; the
  // other sees the guard and skips, or gets the server-side cached result for
  // free). Idempotent and fully non-blocking.
  const earlyAugmentFiredRef = useRef(false)
  useEffect(() => {
    if (!isGeneralSpecialty) return
    if (category !== 'details') return
    if (!currentCase?.id) return
    if (earlyAugmentFiredRef.current) return
    earlyAugmentFiredRef.current = true

    console.log('[DescribeSection] Early-firing details augment for General (loader window)')
    startAugment({ caseId: currentCase.id, category: 'details', language, fetchAugment: aiAPI.augmentCategory })
  }, [isGeneralSpecialty, category, currentCase?.id, language])

  // Check if describe is already complete (locked)
  // v242.235.0: answers lock once the summary is written (utils/describeLock.js owns the rule).
  const isLocked = isDescribeLocked(currentCase)
  
  // Handle delete case - actually delete from storage
  const handleDeleteCase = async () => {
    // DELETE-CURRENTCASE-NULL-1 — NAVIGATE FIRST, DELETE SECOND.
    // deleteCase() sets currentCase to null in the store. Awaiting it here re-rendered
    // THIS screen against that null while it was still mounted, and the first thing in
    // render that reads currentCase.<x> without a ?. threw a React error. The delete and
    // the navigation both completed, which is exactly why it looked cosmetic: the app
    // "worked" and threw anyway. Unmounting first removes the whole class, at every one
    // of the six screens that can delete their own case, rather than optional-chaining
    // whichever line happened to fire (RULES §1bv(a)).
    const id = currentCase?.id
    setShowDeleteModal(false)
    navigate('/home', { replace: true })
    if (id) {
      console.log('[DescribeSection] Deleting case:', id)
      // ★ PROD-416: a delete the server refuses leaves the case, and the patient is told.
      try { await deleteCase(id) } catch (err) { reportDeleteFailure(t, err) }
    } else {
      clearCurrentCase()
    }
  }
  
  // Handle save as draft - save current state and go to cases
  const handleSaveAsDraft = async () => {
    if (currentCase?.id) {
      console.log('[DescribeSection] Saving case as draft:', currentCase.id)
      await updateAndSaveCase({ 
        status: 'draft',
        answers: { ...currentCase.answers, ...answers }
      })
    }
    setShowDeleteModal(false)
    navigate('/cases', { replace: true })
  }

  // Step configuration with translation keys
  // v161.0.0: Added adaptive step between additional and review (step 6 of 7)
  const STEPS = {
    'details': { num: 2, titleKey: 'describe.details', next: '/case/describe/signs-symptoms', prev: '/case/describe/voice' },
    'signs-symptoms': { num: 3, titleKey: 'describe.signsSymptoms', next: '/case/describe/context', prev: '/case/describe/details' },
    'context': { num: 4, titleKey: 'describe.context', next: '/case/describe/additional', prev: '/case/describe/signs-symptoms' },
    'additional': { num: 5, titleKey: 'describe.additionalFactors', next: '/case/describe/adaptive', prev: '/case/describe/context' },
  }

  const CATEGORY_MAP = {
    'details': 'details',
    'signs-symptoms': 'signs_symptoms',
    'context': 'context',
    'additional': 'additional_factors',
  }

  const stepInfo = STEPS[category]
  const categoryKey = CATEGORY_MAP[category]

  // Scroll to top on mount and when category changes
  useEffect(() => {
    window.scrollTo(0, 0)
    // Only reset showContent if this step's animation hasn't played yet
    const key = `orella_chat_describe_${category}_${currentCase?.id}`
    if (!sessionStorage.getItem(key)) {
      setShowContent(false)
    } else {
      setShowContent(true) // Immediately show for already-played animations
    }
  }, [category])

  // v213.9 (Sprint 13C-A2): Fetch prior-category AI augmentation when entering this
  // screen. Only fires for 'general' specialty cases. Result is rendered appended
  // below the static anchors (the "few more" section). Cached in sessionStorage so
  // back-nav doesn't re-fire the call.
  useEffect(() => {
    if (!isGeneralSpecialty) return
    if (!prevCategoryKey) {
      setAugmentQs([])
      return
    }
    if (!currentCase?.id) return

    const fetchKey = `${currentCase.id}__${prevCategoryKey}`
    if (augmentFetchedForRef.current === fetchKey) return
    augmentFetchedForRef.current = fetchKey

    // Try sessionStorage cache first (set by handleNext on the previous screen)
    const cacheKey = `orella_augment_${currentCase.id}_${prevCategoryKey}`
    try {
      const cached = sessionStorage.getItem(cacheKey)
      if (cached) {
        const parsed = JSON.parse(cached)
        if (Array.isArray(parsed?.questions) && parsed.questions.length > 0) {
          setAugmentQs(parsed.questions)
          console.log(`[DescribeSection] ✅ Loaded ${parsed.questions.length} augment Qs for ${prevCategoryKey} from sessionStorage`)
          return
        }
      }
    } catch (e) {
      console.warn('[DescribeSection] augment cache read failed:', e?.message)
    }

    // Cache miss — join the call handleNext started on the previous screen (PROD-1227: it used
    // to start a second one here, before the answers were saved), or start it now. Server-side
    // cache returns a prior result for this case+category, so this is idempotent.
    setAugmentLoading(true)
    startAugment({ caseId: currentCase.id, category: prevCategoryKey, language, fetchAugment: aiAPI.augmentCategory })
      .then((result) => {
        if (Array.isArray(result?.questions) && result.questions.length > 0) {
          setAugmentQs(result.questions)
          console.log(`[DescribeSection] ✅ Augment fetched: ${result.questions.length} Qs for ${prevCategoryKey}`)
        } else {
          // no questions, or the call failed / timed out (non-fatal) — skip the section
          console.log(`[DescribeSection] Augment returned no questions for ${prevCategoryKey} — skipping section`)
          setAugmentQs([])
        }
      })
      .finally(() => {
        setAugmentLoading(false)
      })
  }, [isGeneralSpecialty, prevCategoryKey, currentCase?.id, language])

  // v219.5.6 (Sprint 13C-A): Transcript pre-fill for the resolved augment
  // questions. The anchors already get voice pre-fill (extractSurveyAnswers is
  // questionnaire-agnostic and runs on the General path's fixed anchors too);
  // this closes the gap where the AI-generated augment questions got ZERO
  // pre-fill and were hand-answered even when the voice memo already answered
  // them.
  //
  // Design: fires ONCE per (case, prevCategory) the moment augmentQs becomes
  // non-empty — this single watcher covers BOTH the sessionStorage cache-hit
  // branch and the fresh-fetch branch above without duplicating logic. The
  // call is non-blocking and slots into the latency window the patient is
  // already spending on this screen's anchor questions, so there is no added
  // perceived latency. The server runs the SAME, UNMODIFIED Sonnet extractor
  // (same SURVEY_EXTRACTION_PROMPT, same negation / speaker-attribution /
  // named-disease HARD LIMIT / HIGH-only gating) — augment Qs are just the
  // questionnaire this time. Returned answers are keyed
  // `{prevCategoryKey}_augment_{augQid}` — byte-identical to
  // getAugmentAnswerKey — so they merge straight into `answers` with no
  // remapping. Existing patient-entered answers ALWAYS win (prefill fills
  // blanks only), exactly like the anchor path. On any failure the patient
  // just answers the augment questions themselves (unchanged from today).
  const augmentPrefillFiredRef = useRef(null)
  useEffect(() => {
    if (!isGeneralSpecialty) return
    if (!prevCategoryKey) return
    if (!currentCase?.id) return
    if (!Array.isArray(augmentQs) || augmentQs.length === 0) return

    // Need a transcript to extract from — no transcript means nothing to
    // pre-fill (patient answers by hand, unchanged).
    const haveTranscript = !!(
      (currentCase?.voiceTranscript && currentCase.voiceTranscript.trim()) ||
      (currentCase?.voiceTextInput && currentCase.voiceTextInput.trim())
    )
    if (!haveTranscript) return

    // Fire at most once per (case, prevCategory, augment-question set). Back-nav
    // and re-renders are guarded; a changed augment set re-fires (server also
    // re-validates via the augQids hash, so this is doubly safe).
    const augIds = augmentQs.map(q => q?.id).filter(Boolean).join(',')
    const fireKey = `${currentCase.id}__${prevCategoryKey}__${augIds}`
    if (augmentPrefillFiredRef.current === fireKey) return
    augmentPrefillFiredRef.current = fireKey

    let cancelled = false
    aiAPI.extractAugmentAnswers(
      currentCase.id, prevCategoryKey, augmentQs, language || 'en'
    )
      .then((result) => {
        if (cancelled) return
        const aiAnswers = result && result.answers ? result.answers : null
        if (!aiAnswers || Object.keys(aiAnswers).length === 0) {
          console.log(`[DescribeSection] Augment pre-fill: no high-confidence answers for ${prevCategoryKey}`)
          return
        }
        // v224.7.4 FIX: SOURCE-AWARE strip. This pass owns ONLY the augment-ai
        // keys it is regenerating right now (the fresh `aiAnswers`). Everything
        // else — voice-ai anchor answers, profile prefills, augment-ai for
        // OTHER categories, and user-entered answers — MUST be preserved. The
        // previous logic kept only UNTAGGED keys, which wiped the voice-ai
        // anchor answers on the General path (same root cause as the profile
        // pass). User-entered always wins, even over a fresh AI augment answer.
        setAnswers(prev => {
          const existingPrefillKeys = currentCase?.prefillKeys || {}
          const preserved = {}
          for (const [k, v] of Object.entries(prev)) {
            const isUserEntered = !existingPrefillKeys[k]
            const beingRegenerated = (k in aiAnswers)
            // Keep if the patient entered it (always), or if this pass is not
            // regenerating it. Only prior augment-ai values we are refreshing
            // get dropped.
            if (isUserEntered || !beingRegenerated) preserved[k] = v
          }
          // Fresh augment AI fills blanks; preserved (incl. user-entered) wins.
          const merged = { ...aiAnswers, ...preserved }
          const newPrefillKeys = { ...existingPrefillKeys }
          // Drop tags whose values are no longer present.
          for (const k of Object.keys(existingPrefillKeys)) {
            if (!(k in merged)) delete newPrefillKeys[k]
          }
          // Tag as augment-ai only the keys the AI value actually populated —
          // i.e. NOT the ones a patient had already answered (those stay
          // user-entered / untagged so future passes never overwrite them).
          for (const k of Object.keys(aiAnswers)) {
            const wasUserEntered = (k in prev) && !existingPrefillKeys[k]
            if (!wasUserEntered) newPrefillKeys[k] = 'augment-ai'
          }
          try { updateCase({ answers: merged, prefillKeys: newPrefillKeys }) } catch (_) {}
          return merged
        })
        console.log(`[DescribeSection] ✅ Augment pre-fill: ${Object.keys(aiAnswers).length} high-confidence answer(s) for ${prevCategoryKey}`)
      })
      .catch((err) => {
        // Non-fatal by contract — patient just answers the augment Qs.
        console.warn(`[DescribeSection] Augment pre-fill failed for ${prevCategoryKey} (non-fatal):`, err?.message)
      })

    return () => { cancelled = true }
  }, [isGeneralSpecialty, prevCategoryKey, currentCase?.id, augmentQs, language])

  // Hydrate answers from currentCase
  // ★ PROD-922: keyed on the ANSWERS object, not on the whole case. A store write that leaves the answers
  //   alone (untagPrefill's prefillKeys, questionText) used to reset the screen to the store's answers and
  //   so dropped the click that had just been made on a prefilled field (the click lives in local state
  //   until Next/Back). Every write that changes answers, and a case switch, still re-hydrates.
  useEffect(() => {
    if (currentCase?.answers && Object.keys(currentCase.answers).length > 0) {
      setAnswers(currentCase.answers)
    }
  }, [currentCase?.answers])

  // v151.3.0: If answers are empty but case has a server ID (draft resume), fetch fresh from API
  const [fetchingDraft, setFetchingDraft] = useState(false)
  // ★ PROD-922 (r2_922): whether this screen is still mounted. Every Describe step is its own Route, so Next,
  //   Back or home unmounts it, and the page is interactive while the draft fetch is in flight.
  const mountedRef = useRef(false)
  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])
  useEffect(() => {
    const caseId = currentCase?.id
    if (!caseId || caseId.startsWith('local-') || caseId.startsWith('case')) return
    if (currentCase?.answers && Object.keys(currentCase.answers).length > 0) return
    if (fetchingDraft) return
    
    const fetchFresh = async () => {
      setFetchingDraft(true)
      try {
        const { casesAPI } = await import('../../services/api')
        const freshCase = await casesAPI.get(caseId)
        if (freshCase) {
          const freshAnswers = stripQuestionText(freshCase.questionnaire_responses || freshCase.answers) || {}
          if (Object.keys(freshAnswers).length > 0) {
            // ★ PROD-922: MERGE over what this mount holds by now (the profile prefill of the same mount
            //   finished first; `currentCase` here is the stale mount snapshot, so read the store). Saved
            //   answers win over prefills and drop their tags; other prefills and in-flight answers stay.
            //   The merge runs over the SCREEN state (`prev`): the click handlers write a value to local
            //   state only (the store gets answers on Next/Back), so an answer the patient clicked during
            //   the fetch exists only there. A different case made current during the fetch is not written.
            //   `latest` is read ONCE, here, and never inside the updater (r2_922 S2).
            const latest = useCaseStore.getState().currentCase || {}
            if (latest.id !== caseId) return
            let rehydrated = null
            if (mountedRef.current) {
              // ★ PROD-922 (r2_922 S2): the updater is PURE. It reads only `prev` and the `latest` snapshot
              //   (never the store) and sets nothing but this local result, so a second invocation (React
              //   StrictMode in DEV) computes the same result from the same `prev`. flushSync makes React run
              //   it before the store write below, which writes the same merge the screen holds.
              flushSync(() => {
                setAnswers((prev) => {
                  rehydrated = mergeRehydratedAnswers({ ...(latest.answers || {}), ...(prev || {}) }, latest.prefillKeys, freshAnswers)
                  return rehydrated.answers
                })
              })
            }
            if (!rehydrated) {
              // ★ PROD-922 (r2_922 S1): the patient left the step before the fetch resolved (Next, Back or
              //   home unmount it). React never runs an updater of an unmounted screen, and the next mount
              //   does not fetch again once the store holds any answers (the profile prefill, or the Next
              //   that left, wrote some). So merge the saved answers straight into the store's answers.
              //   An answer clicked on the screen is there only if Next wrote it; Back discards it, as before.
              rehydrated = mergeRehydratedAnswers(latest.answers, latest.prefillKeys, freshAnswers)
            }
            // Also update the store so it persists
            try { updateCase({ answers: rehydrated.answers, prefillKeys: rehydrated.prefillKeys }) } catch (_) {}
            console.log('[DescribeSection] Rehydrated', Object.keys(freshAnswers).length, 'answer keys from API')
          }
        }
      } catch (err) {
        console.warn('[DescribeSection] Draft fetch failed:', err)
      } finally {
        setFetchingDraft(false)
      }
    }
    fetchFresh()
  }, [currentCase?.id])

  // v175.2.0: Profile-aware prefill — runs once when entering questionnaire
  // Merges profile conditions/meds/allergies into answers without overwriting existing answers.
  //
  // v220.11: Uses the prefillKeys map (same as DescribeVoice). Stale prefill
  // values are stripped before merging; user-entered answers (not in
  // prefillKeys) are preserved. New profile-sourced answers are tagged
  // 'profile' so subsequent runs can replace them, and click handlers can
  // promote them to user-entered.
  useEffect(() => {
    if (profilePrefillDoneRef.current) return
    if (!currentCase?.specialtyId || !currentCase?.focusAreaId) return
    if (!profile?.medicalHistory) return
    const existingAnswers = currentCase?.answers || {}
    const existingPrefillKeys = currentCase?.prefillKeys || {}
    // Only prefill if there aren't many USER-ENTERED answers yet. Stale
    // prefills don't count toward this threshold — without this, a poisoned
    // localStorage with N stale prefills would skip the profile pass on a
    // fresh test, perpetuating the bug.
    const userEnteredCount = Object.keys(existingAnswers)
      .filter(k => !existingPrefillKeys[k]).length
    if (userEnteredCount > 5) return

    try {
      const { getQuestions: gq } = useQuestionnaireStore.getState()
      const allQuestions = getAllQuestionsForCase(gq, currentCase.specialtyId, currentCase.focusAreaId)
      const { answers: profileAnswers, prefillCount } = prefillFromProfile(profile, allQuestions)
      if (prefillCount > 0) {
        // v224.7.4 FIX: SOURCE-AWARE strip. This pass owns ONLY 'profile'
        // prefills, so only those may be treated as stale here. The previous
        // logic kept only UNTAGGED keys (user-entered), which silently
        // discarded the fresh voice-ai answers DescribeVoice had just written
        // whenever this effect ran after them (the lost-AI-fix regression —
        // bit the voice path because voice fires extraction early, on
        // transcription, so the AI answers were already committed by the time
        // this mounted). Voice-ai / voice-regex / augment-ai prefills AND
        // user-entered answers are now all preserved.
        const preserved = {}
        for (const [k, v] of Object.entries(existingAnswers)) {
          if (existingPrefillKeys[k] !== 'profile') preserved[k] = v
        }
        // preserved (incl. AI prefills + user-entered) WINS over profile;
        // profile only fills keys nothing else has touched.
        const merged = { ...profileAnswers, ...preserved }
        // prefillKeys: carry forward every surviving non-'profile' tag, then
        // tag the profile-filled blanks WITHOUT clobbering a surviving tag.
        const newPrefillKeys = {}
        for (const [k, tag] of Object.entries(existingPrefillKeys)) {
          if (tag !== 'profile' && k in merged) newPrefillKeys[k] = tag
        }
        // ★ PROD-922: only the blanks profile actually filled — a key `preserved` won holds the
        //   patient's (or saved) value, and a 'profile' tag on it would let the next pass strip it.
        for (const k of Object.keys(profileAnswers)) {
          if (!(k in newPrefillKeys) && !(k in preserved)) newPrefillKeys[k] = 'profile'
        }
        setAnswers(merged)
        updateCase({ answers: merged, prefillKeys: newPrefillKeys, hasPrefill: true })
        console.log(`[DescribeSection] ✅ Profile prefill: ${prefillCount} answers from profile data`)
      }
    } catch (err) {
      console.warn('[DescribeSection] Profile prefill skipped:', err?.message)
    }
    profilePrefillDoneRef.current = true
  }, [currentCase?.specialtyId, currentCase?.focusAreaId, profile])

  // Get questions for current category from store (must be before early return — hooks rule)
  const { getSpecialty, getFocusArea } = useQuestionnaireStore()

  // PROD-184 — record the TEXT of every question this case can show (all anchor
  // categories of the runtime questionnaire + every augment set seen so far) on the
  // case, LOCAL ONLY. The store attaches it to questionnaireResponses at the API
  // boundary (withQuestionText), so the MD panel no longer needs a bundled copy.
  const questionTextFocusArea = currentCase ? getFocusArea(currentCase.specialtyId, currentCase.focusAreaId) : null
  useEffect(() => {
    if (!currentCase?.id) return
    const augmentByCategory = {}
    try {
      for (const cat of AUGMENT_CATEGORIES) {
        const raw = sessionStorage.getItem(`orella_augment_${currentCase.id}_${cat}`)
        const parsed = raw ? JSON.parse(raw) : null
        if (Array.isArray(parsed?.questions)) augmentByCategory[cat] = parsed.questions
      }
    } catch (_) { /* sessionStorage unavailable — augmentQs below still counts */ }
    if (prevCategoryKey && augmentQs.length > 0) augmentByCategory[prevCategoryKey] = augmentQs
    const entries = buildQuestionTextEntries(questionTextFocusArea?.questions, augmentByCategory)
    if (entries.length === 0) return
    const merged = mergeQuestionText(currentCase.questionText, entries)
    const before = JSON.stringify(currentCase.questionText || [])
    if (JSON.stringify(merged) !== before) updateCase({ questionText: merged })
  }, [currentCase?.id, questionTextFocusArea, prevCategoryKey, augmentQs])

  if (!currentCase || !stepInfo) {
    navigate('/home')
    return null
  }

  const specialty = getSpecialty(currentCase.specialtyId)
  const focusArea = getFocusArea(currentCase.specialtyId, currentCase.focusAreaId)
  const allQuestions = focusArea?.questions?.[categoryKey] || []
  const facts = patientFacts(profile)
  const questions = allQuestions.filter((q) => isQuestionShown(q, facts, answers))
  
  // Debug logging
  console.log('[DescribeSection] Loading questions:', {
    specialtyId: currentCase.specialtyId,
    focusAreaId: currentCase.focusAreaId,
    categoryKey,
    specialtyFound: !!specialty,
    focusAreaFound: !!focusArea,
    questionCount: questions.length
  })

  // Use category-prefixed keys for answers to avoid collision between categories
  const getAnswerKey = (questionId) => `${categoryKey}_${questionId}`

  // s100: the card for each question comes from utils/answerAlerts.js (the one owner).
  const alertFocusId = currentCase?.focusAreaId
  const alertFor = (questionId) => {
    const k = getAnswerKey(questionId)
    return alertCard(alertIdFor(alertFocusId, k, answers[k]), language)
  }
  const pendingAlertAck = !isLocked && questions.some((q) => {
    const card = alertFor(q.id)
    return card?.ack && alertAcks[getAnswerKey(q.id)] !== card.id
  })

  // v220.11: When the patient touches a question, promote that key from
  // prefill-sourced to user-entered by dropping it from prefillKeys. This is
  // what makes future prefill runs treat it as untouchable. Idempotent: if
  // the key wasn't in prefillKeys it's a cheap no-op write.
  const untagPrefill = (answerKey) => {
    try {
      const cur = currentCase?.prefillKeys || {}
      if (!cur[answerKey]) return
      const next = { ...cur }
      delete next[answerKey]
      updateCase({ prefillKeys: next })
    } catch (_) { /* non-fatal */ }
  }

  // Check if an option is a "none" type option
  const isNoneOption = (option) => {
    const lower = option.toLowerCase()
    return lower.includes('none of these') || 
           lower.includes('none of the above') || 
           lower === 'none' ||
           lower.includes('no symptoms') ||
           lower.includes('not experiencing any')
  }

  // Handle multi-select (checkboxes) with "None of these" logic
  const handleToggleOption = (questionId, option, allOptions = []) => {
    if (isLocked) return // Prevent changes when locked
    const answerKey = getAnswerKey(questionId)
    untagPrefill(answerKey)

    setAnswers(prev => {
      const currentAnswers = prev[answerKey] || []
      let newAnswers
      
      if (currentAnswers.includes(option)) {
        // Unchecking - just remove the option
        newAnswers = currentAnswers.filter(o => o !== option)
      } else {
        // Checking a new option
        if (isNoneOption(option)) {
          // If selecting "None of these", clear all other selections
          newAnswers = [option]
        } else {
          // If selecting a regular option, remove any "None of these" option
          const filteredAnswers = currentAnswers.filter(o => !isNoneOption(o))
          newAnswers = [...filteredAnswers, option]
        }
      }
      
      return { ...prev, [answerKey]: newAnswers }
    })
  }

  // Handle single-select (radio buttons)
  const handleSingleSelect = (questionId, option) => {
    if (isLocked) return // Prevent changes when locked
    const answerKey = getAnswerKey(questionId)
    untagPrefill(answerKey)
    setAnswers(prev => ({ ...prev, [answerKey]: [option] }))
  }

  // Handle text input
  const handleTextInput = (questionId, text) => {
    if (isLocked) return // Prevent changes when locked
    const answerKey = getAnswerKey(questionId)
    untagPrefill(answerKey)
    setAnswers(prev => ({ ...prev, [answerKey]: text }))
  }

  // Handle scale selection
  const handleScaleSelect = (questionId, value) => {
    if (isLocked) return // Prevent changes when locked
    const answerKey = getAnswerKey(questionId)
    untagPrefill(answerKey)
    setAnswers(prev => ({ ...prev, [answerKey]: [String(value)] }))
  }

  // Handle photo capture
  const handlePhotoCapture = (questionId) => {
    if (isLocked) return // Prevent changes when locked
    // For now, just mark as "photo taken" - in production this would open camera
    const answerKey = getAnswerKey(questionId)
    untagPrefill(answerKey)
    setAnswers(prev => ({ ...prev, [answerKey]: ['Photo captured'] }))
  }

  const getAnswersForQuestion = (questionId) => {
    const answerKey = getAnswerKey(questionId)
    const answer = answers[answerKey]
    if (Array.isArray(answer)) return answer
    if (typeof answer === 'string') return [answer]
    return []
  }

  const getTextAnswer = (questionId) => {
    const answerKey = getAnswerKey(questionId)
    const answer = answers[answerKey]
    return typeof answer === 'string' ? answer : ''
  }

  // v213.9 (Sprint 13C-A2): Augment question helpers — separate namespace for AI Qs
  // to avoid colliding with anchor keys. Stored as `${prevCategoryKey}_augment_${qId}`.
  const getAugmentAnswerKey = (qId) => `${prevCategoryKey}_augment_${qId}`

  const getAugmentAnswers = (qId) => {
    const k = getAugmentAnswerKey(qId)
    const v = answers[k]
    if (Array.isArray(v)) return v
    if (typeof v === 'string') return [v]
    return []
  }

  const getAugmentText = (qId) => {
    const v = answers[getAugmentAnswerKey(qId)]
    return typeof v === 'string' ? v : ''
  }

  const handleAugSingle = (qId, option) => {
    if (isLocked) return
    untagPrefill(getAugmentAnswerKey(qId))
    setAnswers(prev => ({ ...prev, [getAugmentAnswerKey(qId)]: [option] }))
  }

  const handleAugToggle = (qId, option) => {
    if (isLocked) return
    const k = getAugmentAnswerKey(qId)
    untagPrefill(k)
    setAnswers(prev => {
      const current = prev[k] || []
      let next
      if (current.includes(option)) {
        next = current.filter(o => o !== option)
      } else if (isNoneOption(option)) {
        next = [option]
      } else {
        next = [...current.filter(o => !isNoneOption(o)), option]
      }
      return { ...prev, [k]: next }
    })
  }

  const handleAugScale = (qId, value) => {
    if (isLocked) return
    untagPrefill(getAugmentAnswerKey(qId))
    setAnswers(prev => ({ ...prev, [getAugmentAnswerKey(qId)]: [String(value)] }))
  }

  const handleAugText = (qId, text) => {
    if (isLocked) return
    untagPrefill(getAugmentAnswerKey(qId))
    setAnswers(prev => ({ ...prev, [getAugmentAnswerKey(qId)]: text }))
  }

  const handleNext = () => {
    if (pendingAlertAck) return // s100: a red card is waiting for "I understand"
    console.log('=== DescribeSection handleNext ===')
    console.log('Category:', categoryKey)
    console.log('Local answers keys:', Object.keys(answers))
    console.log('Answers with values:', Object.entries(answers).filter(([k, v]) => v && (Array.isArray(v) ? v.length > 0 : true)).map(([k]) => k))
    const answersSaved = updateAndSaveCase({ answers: dropHiddenAnswers(answers, allQuestions, categoryKey, facts) })
    
    // v161.0.0: Fire ADAPTIVE QUESTIONS early when leaving CONTEXT section (step 4)
    // Uses Sonnet for quality. Patient still has Additional Factors to complete (~30-60s),
    // giving the AI enough time. Result cached in sessionStorage for DescribeAdaptive pickup.
    // v226.10.0: NOT for the general path — general narrows via the four augment passes and
    // never displays an adaptive set, so firing this only burned a Sonnet call and wrote a
    // never-answered phantom question set onto the case. The server also short-circuits
    // general defensively; this is the primary guard.
    if (category === 'context' && currentCase?.id && !isGeneralSpecialty) {
      const cacheKey = `orella_adaptive_inflight_${currentCase.id}`
      if (!sessionStorage.getItem(cacheKey)) {
        sessionStorage.setItem(cacheKey, 'true')
        console.log('[DescribeSection] Firing early adaptive questions for case:', currentCase.id.slice(-8))
        setTimeout(() => {
          aiAPI.generateAdaptiveQuestions(currentCase.id, language)
            .then((result) => {
              if (result?.questions?.length >= 2) {
                updateCase({ adaptiveQuestions: result, adaptive_questions: result })
                sessionStorage.setItem(`orella_adaptive_result_${currentCase.id}`, JSON.stringify(result))
                console.log('[DescribeSection] ✅ Adaptive questions pre-generated:', result.questions.length, 'Qs')
              }
              sessionStorage.removeItem(cacheKey)
            })
            .catch((err) => {
              sessionStorage.removeItem(cacheKey)
              console.log('[DescribeSection] Early adaptive questions failed (DescribeAdaptive will retry):', err?.message)
            })
        }, 1500)
      }
    }
    
    // v162.9.0: Patient summary fire moved to DescribeAdaptive (after adaptive answers are saved)
    // Previously fired here at 'additional' step, but adaptive questions now come after additional,
    // so the summary was generated with incomplete data.

    // v213.9 (Sprint 13C-A2): For 'general' specialty cases, fire augmentation for the
    // category we're LEAVING so it's pre-warmed when the next screen mounts. We pass
    // categoryKey (underscore form, e.g. 'signs_symptoms') to the API.
    // PROD-1227: it fires as soon as these answers are saved (at most 800 ms, the old fixed
    // delay), and the next screen joins this same call instead of starting its own.
    if (isGeneralSpecialty && currentCase?.id && categoryKey) {
      console.log(`[DescribeSection] Pre-warming augment for ${categoryKey} (general specialty)`)
      startAugment({ caseId: currentCase.id, category: categoryKey, language, fetchAugment: aiAPI.augmentCategory, afterSaved: answersSaved })
    }

    navigate(stepInfo.next)
  }

  const handleSkipToReview = () => {
    if (pendingAlertAck) return
    console.log('=== DescribeSection handleSkipToReview ===')
    console.log('Local answers keys:', Object.keys(answers))
    updateAndSaveCase({ answers: dropHiddenAnswers(answers, allQuestions, categoryKey, facts) })
    // v213.3 (C-A-189): Skip jumps straight to the View Summary page (where the
    // patient submits) instead of the intermediate Review section list.
    navigate('/case/describe/summary')
  }

  const handleBack = () => {
    navigate(stepInfo.prev)
  }

  // Chat message per step
  // v162.9.0: If voice prefill happened, show a note on the first survey step
  const hasPrefill = currentCase?.voicePrefilled && currentCase?.voicePrefillCount > 0
  const prefillMsg = t('describe.chatDetailsPrefill') !== 'describe.chatDetailsPrefill'
    ? t('describe.chatDetailsPrefill')
    : "Based on what you shared, I've marked a few responses already — feel free to change anything that doesn't look right."
  
  const CHAT_MESSAGES = {
    'details': (hasPrefill && category === 'details') ? prefillMsg : t('describe.chatDetails'),
    'signs-symptoms': t('describe.chatSigns'),
    'context': t('describe.chatContext'),
    'additional': t('describe.chatAdditional'),
  }

  // v225.25.0 (Item 1): extraction loader copy (i18n with safe fallbacks).
  const extractingMessage =
    t('describe.extractingAnswers') !== 'describe.extractingAnswers'
      ? t('describe.extractingAnswers')
      : 'Reviewing what you shared\u2026'
  const extractingSubMessage =
    t('describe.extractingAnswersSub') !== 'describe.extractingAnswersSub'
      ? t('describe.extractingAnswersSub')
      : 'Marking responses so you have less to fill in.'

  return (
    <div className="tabbar-screen-min-h bg-white flex flex-col">
      <ChatKeyframes />
      
      {/* Compact Header */}
      <CompactHeader
        stepNum={stepInfo.num}
        totalSteps={7}
        caseInfo={`${currentCase.specialtyName} › ${currentCase.focusAreaName}`}
        statusLabel={currentCase.status === 'finalized' ? t('caseHome.statusFinalized') : 
          currentCase.status === 'claimed' ? t('caseHome.statusClaimed') :
          currentCase.status === 'shared' ? t('caseHome.statusShared') :
          currentCase.status === 'submitted' ? t('cases.statusSubmitted') : t('caseHome.statusInProgress')}
        onBack={handleBack}
        onNotification={() => navigate('/notifications')}
      />

      {/* v219.5.1 / v225.25.0: Survey-extraction loader — shown on the first
          category while Sonnet pre-fills answers from the voice memo, so the
          patient never sees an empty questionnaire that fills in late. Rendered
          as a conversational Orella "thinking" bubble + questionnaire skeleton
          (see block below) rather than a centered spinner. */}
      {showExtractionLoader ? (
        <div className="flex-1 px-4 pt-4 pb-4 overflow-auto bg-white">
          {/* v225.25.0 (Item 1): the extraction wait is now a beat in the same
              conversation as the rest of Describe — an Orella "thinking" bubble
              (spinning ring woven into the avatar + typing dots) grounded at the
              top, with a faint skeleton of the questionnaire below so the patient
              can see what's about to appear. Persists until extraction resolves
              (surveyExtractionDone) or the 30s safety cap. */}
          <div style={{ animation: 'fadeSlideUp 0.3s ease' }}>
            <OrellaThinkingBubble message={extractingMessage} subMessage={extractingSubMessage} />
          </div>

          {/* Faint questionnaire skeleton — previews what's loading */}
          <div className="mt-5" style={{ animation: 'orellaSkelPulse 1.8s ease-in-out infinite' }} aria-hidden="true">
            <div className="h-[11px] w-[55%] rounded-md mb-3" style={{ background: '#EEF2F6' }} />
            {[0, 1].map((i) => (
              <div key={i} className="rounded-xl p-3.5 mb-2.5" style={{ border: '1px solid #EEF2F6' }}>
                <div className="h-[10px] w-[70%] rounded-md mb-2.5" style={{ background: '#F1F5F9' }} />
                <div className="h-[38px] rounded-lg" style={{ background: '#F8FAFC', border: '1px solid #F1F5F9' }} />
              </div>
            ))}
          </div>
        </div>
      ) : (
      <>
      {/* Orella Chat */}
      <OrellaChatBubble
        message={CHAT_MESSAGES[category] || t('describe.chatDetails')}
        animationKey={`describe_${category}_${currentCase?.id}`}
        onComplete={() => setShowContent(true)}
      />

      {/* Content area */}
      <div 
        className="flex-1 bg-white"
        style={{ 
          transition: 'opacity 0.4s ease, transform 0.4s ease',
          opacity: showContent ? 1 : 0,
          transform: showContent ? 'translateY(0)' : 'translateY(12px)'
        }}
      >
        <div className="px-4 pt-2 flex-shrink-0">
          {/* Locked Banner */}
          {isLocked && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-1 mb-2 flex items-start gap-3">
              <Lock className="text-amber-500 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="text-amber-700 font-medium">{t('describe.sectionLocked')}</p>
                <p className="text-amber-600 text-sm">{t('describe.viewOnlyMode')}</p>
              </div>
            </div>
          )}
        </div>

        {/* Questions - Scrollable */}
        <div className="flex-1 px-4 pb-4">
        {questions.length === 0 ? (
          <p className="text-gray-500 text-center py-8">{t('describe.noQuestions')}</p>
        ) : (
          <div className="space-y-6">
            {questions.map((question, qIndex) => (
              <div key={question.id} className="border-b border-gray-100 pb-6 last:border-0">
                {/* Question Number and Text */}
                <p className="font-medium text-gray-800 mb-3">
                  <span className="text-orella-500">{qIndex + 1}.</span> {tq(question.text)}
                </p>

                {/* Options - Render based on question type */}
                <div className="space-y-2">
                  {/* Single Select (Radio Buttons) */}
                  {(question.type === 'single-select' || !question.type) && visibleOptions(question, facts).map((option) => {
                    const isSelected = getAnswersForQuestion(question.id).includes(option)
                    
                    return (
                      <button
                        key={option}
                        onClick={() => handleSingleSelect(question.id, option)}
                        className={`w-full p-3 rounded-xl border text-left flex items-center gap-3 transition-all ${
                          isSelected
                            ? 'border-orella-500 bg-orella-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          isSelected
                            ? 'border-orella-500'
                            : 'border-gray-300'
                        }`}>
                          {isSelected && <Circle size={10} className="text-orella-500 fill-orella-500" />}
                        </div>
                        <span className={`text-sm ${isSelected ? 'text-orella-700' : 'text-gray-700'}`}>
                          {to(option)}
                        </span>
                      </button>
                    )
                  })}

                  {/* Multi Select (Checkboxes) */}
                  {question.type === 'multi-select' && visibleOptions(question, facts).map((option) => {
                    const isSelected = getAnswersForQuestion(question.id).includes(option)
                    const isNone = isNoneOption(option)
                    
                    return (
                      <button
                        key={option}
                        onClick={() => handleToggleOption(question.id, option)}
                        className={`w-full p-3 rounded-xl border text-left flex items-center gap-3 transition-all ${
                          isSelected
                            ? 'border-orella-500 bg-orella-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {isNone ? (
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                            isSelected ? 'border-orella-500' : 'border-gray-300'
                          }`}>
                            {isSelected && <Circle size={10} className="text-orella-500 fill-orella-500" />}
                          </div>
                        ) : (
                          <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${
                            isSelected ? 'bg-orella-500 border-orella-500' : 'border-gray-300'
                          }`}>
                            {isSelected && <Check size={14} className="text-white" />}
                          </div>
                        )}
                        <span className={`text-sm ${isSelected ? 'text-orella-700' : 'text-gray-700'}`}>
                          {to(option)}
                        </span>
                      </button>
                    )
                  })}

                  {/* Scale (1-10) */}
                  {question.type === 'scale' && (
                    <div>
                      <div className="grid grid-cols-10 gap-1">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => {
                          const isSelected = getAnswersForQuestion(question.id).includes(String(num))
                          return (
                            <button
                              key={num}
                              onClick={() => handleScaleSelect(question.id, num)}
                              className={`aspect-square rounded-full border-2 flex items-center justify-center text-sm font-semibold transition-all ${
                                isSelected
                                  ? 'border-orella-500 bg-orella-500 text-white'
                                  : 'border-gray-200 text-gray-600 hover:border-gray-300'
                              }`}
                            >
                              {num}
                            </button>
                          )
                        })}
                      </div>
                      <div className="flex justify-between text-xs text-gray-400 mt-2 px-1">
                        <span>{t('describe.scaleLow')}</span>
                        <span>{t('describe.scaleHigh')}</span>
                      </div>
                    </div>
                  )}

                  {/* Free Text Input */}
                  {/* v214.9.0 (Bug 1): Accept both 'text' and 'free-text' — General specialty Q1
                     uses 'free-text', all other specialties use 'text'. Without this, the General
                     "what's bothering you most?" question renders with no input field. */}
                  {(question.type === 'text' || question.type === 'free-text') && (
                    <textarea
                      value={getTextAnswer(question.id)}
                      onChange={(e) => handleTextInput(question.id, e.target.value)}
                      onInput={(e) => {
                        // v213.3 (C-A-186): auto-grow so the textarea never has its own
                        // inner scroll — fixes a bug where touch gestures inside the
                        // textarea could not scroll the page past it.
                        const ta = e.currentTarget
                        ta.style.height = 'auto'
                        ta.style.height = Math.min(ta.scrollHeight, 400) + 'px'
                      }}
                      ref={(el) => {
                        if (el && el.scrollHeight > el.clientHeight) {
                          el.style.height = 'auto'
                          el.style.height = Math.min(el.scrollHeight, 400) + 'px'
                        }
                      }}
                      placeholder={t('describe.typeAnswer')}
                      className="w-full p-3 rounded-xl border border-gray-200 focus:border-orella-500 focus:ring-1 focus:ring-orella-500 outline-none resize-none overflow-hidden min-h-[100px] text-gray-700"
                    />
                  )}

                  {/* Photo Upload */}
                  {question.type === 'photo' && (
                    <div className="space-y-3">
                      <button
                        onClick={() => handlePhotoCapture(question.id)}
                        className={`w-full p-4 rounded-xl border-2 border-dashed flex flex-col items-center gap-2 transition-all ${
                          getAnswersForQuestion(question.id).length > 0
                            ? 'border-orella-500 bg-orella-50'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <Camera size={32} className={getAnswersForQuestion(question.id).length > 0 ? 'text-orella-500' : 'text-gray-400'} />
                        <span className={`text-sm font-medium ${getAnswersForQuestion(question.id).length > 0 ? 'text-orella-600' : 'text-gray-500'}`}>
                          {getAnswersForQuestion(question.id).length > 0 ? t('describe.photoAdded') : t('describe.tapAddPhoto')}
                        </span>
                      </button>
                    </div>
                  )}

                  {/* s100: pack-years and Epworth (saved as one English answer each) */}
                  {question.type === 'pack-years' && (
                    <PackYearsInput question={question} value={getTextAnswer(question.id)} onChange={(v) => handleTextInput(question.id, v)} disabled={isLocked} tq={tq} to={to} />
                  )}
                  {question.type === 'epworth' && (
                    <EpworthScale question={question} value={getTextAnswer(question.id)} onChange={(v) => handleTextInput(question.id, v)} disabled={isLocked} tq={tq} to={to} />
                  )}
                  {/* s122: Mental Health scored scales (PHQ-9, GAD-7, …) — one English answer, flags fire the urgent card */}
                  {question.type === 'scored-scale' && (
                    <ScoredScale question={question} value={getTextAnswer(question.id)} onChange={(v) => handleTextInput(question.id, v)} disabled={isLocked} tq={tq} to={to} />
                  )}
                  {question.type === 'number' && (
                    <NumberInput question={question} value={getTextAnswer(question.id)} onChange={(v) => handleTextInput(question.id, v)} disabled={isLocked} to={to} />
                  )}

                  {/* s100: the urgent-answer card opens right under the answer that fires it */}
                  {(() => {
                    const card = alertFor(question.id)
                    if (!card) return null
                    const k = getAnswerKey(question.id)
                    const acked = alertAcks[k] === card.id
                    return <AnswerAlertCard card={card} acknowledged={acked} onAcknowledge={() => ackAlert(k, acked ? null : card.id)} disabled={isLocked} />
                  })()}

                  {/* s107 (catalog 3.8): an answer that means "had this test" - keep a copy, add it in Detect
                      (utils/priorTests.js owns the rule; the Detect row is pinned from the same answer). */}
                  {priorTestTriggered(question, answers[getAnswerKey(question.id)]) && (
                    <div className="mt-2.5 flex items-start gap-2.5 rounded-xl px-3.5 py-3" style={{ background: '#F2FAF3', border: '1px solid #BFE6C3' }}>
                      <FileUp size={18} className="flex-shrink-0 mt-[1px]" style={{ color: '#2F7A38' }} aria-hidden="true" />
                      <p className="text-[13px] leading-snug" style={{ color: '#1F4D25' }}>{t('describe.priorTestNote')}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* v213.9 (Sprint 13C-A2): General-specialty AI augmentation — "A few more"
                section appended below the static anchors. Renders questions generated
                by /ai/augment-category for the PRIOR category. Hidden if not general,
                if no prior category, or if AI returned nothing. */}
            {isGeneralSpecialty && prevCategoryKey && (augmentQs.length > 0 || augmentLoading) && (
              <div className="pt-4 mt-2 border-t border-dashed border-[#A7D9FC]">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-6 h-6 rounded-full bg-[#A7D9FC] flex items-center justify-center flex-shrink-0">
                    <Circle size={8} className="text-[#15354F] fill-[#15354F]" />
                  </div>
                  <p className="text-[13px] font-semibold text-[#15354F] tracking-[0.02em]">
                    {augmentLoading
                      ? t('describe.augmentLoading') || 'A few more from what you shared…'
                      : t('describe.augmentTitle') || 'A few more from what you shared'}
                  </p>
                </div>

                {augmentLoading && augmentQs.length === 0 && (
                  <div className="flex items-center gap-2 pl-1 pb-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#A7D9FC] animate-[dotPulse_1.2s_ease-in-out_infinite]" />
                    <span className="w-1.5 h-1.5 rounded-full bg-[#A7D9FC] animate-[dotPulse_1.2s_ease-in-out_infinite_0.2s]" />
                    <span className="w-1.5 h-1.5 rounded-full bg-[#A7D9FC] animate-[dotPulse_1.2s_ease-in-out_infinite_0.4s]" />
                  </div>
                )}

                <div className="space-y-6">
                  {augmentQs.map((aq, aIdx) => (
                    <div key={aq.id} className="border-b border-gray-100 pb-6 last:border-0">
                      <p className="font-medium text-gray-800 mb-3">
                        <span className="text-orella-500">{questions.length + aIdx + 1}.</span> {tq(aq.text)}
                      </p>
                      <div className="space-y-2">
                        {(aq.type === 'single-select' || !aq.type) && Array.isArray(aq.options) && aq.options.map((option) => {
                          const isSelected = getAugmentAnswers(aq.id).includes(option)
                          return (
                            <button
                              key={option}
                              onClick={() => handleAugSingle(aq.id, option)}
                              className={`w-full p-3 rounded-xl border text-left flex items-center gap-3 transition-all ${
                                isSelected
                                  ? 'border-orella-500 bg-orella-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                                isSelected ? 'border-orella-500' : 'border-gray-300'
                              }`}>
                                {isSelected && <Circle size={10} className="text-orella-500 fill-orella-500" />}
                              </div>
                              <span className={`text-sm ${isSelected ? 'text-orella-700' : 'text-gray-700'}`}>
                                {to(option)}
                              </span>
                            </button>
                          )
                        })}

                        {aq.type === 'multi-select' && Array.isArray(aq.options) && aq.options.map((option) => {
                          const isSelected = getAugmentAnswers(aq.id).includes(option)
                          const isNone = isNoneOption(option)
                          return (
                            <button
                              key={option}
                              onClick={() => handleAugToggle(aq.id, option)}
                              className={`w-full p-3 rounded-xl border text-left flex items-center gap-3 transition-all ${
                                isSelected
                                  ? 'border-orella-500 bg-orella-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              {isNone ? (
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                                  isSelected ? 'border-orella-500' : 'border-gray-300'
                                }`}>
                                  {isSelected && <Circle size={10} className="text-orella-500 fill-orella-500" />}
                                </div>
                              ) : (
                                <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${
                                  isSelected ? 'bg-orella-500 border-orella-500' : 'border-gray-300'
                                }`}>
                                  {isSelected && <Check size={14} className="text-white" />}
                                </div>
                              )}
                              <span className={`text-sm ${isSelected ? 'text-orella-700' : 'text-gray-700'}`}>
                                {to(option)}
                              </span>
                            </button>
                          )
                        })}

                        {aq.type === 'scale' && (
                          <div>
                            <div className="grid grid-cols-10 gap-1">
                              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => {
                                const isSelected = getAugmentAnswers(aq.id).includes(String(num))
                                return (
                                  <button
                                    key={num}
                                    onClick={() => handleAugScale(aq.id, num)}
                                    className={`aspect-square rounded-full border-2 flex items-center justify-center text-sm font-semibold transition-all ${
                                      isSelected
                                        ? 'border-orella-500 bg-orella-500 text-white'
                                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                                    }`}
                                  >
                                    {num}
                                  </button>
                                )
                              })}
                            </div>
                            <div className="flex justify-between text-xs text-gray-400 mt-2 px-1">
                              <span>{t('describe.scaleLow')}</span>
                              <span>{t('describe.scaleHigh')}</span>
                            </div>
                          </div>
                        )}

                        {aq.type === 'free-text' && (
                          <textarea
                            value={getAugmentText(aq.id)}
                            onChange={(e) => handleAugText(aq.id, e.target.value)}
                            onInput={(e) => {
                              const ta = e.currentTarget
                              ta.style.height = 'auto'
                              ta.style.height = Math.min(ta.scrollHeight, 400) + 'px'
                            }}
                            placeholder={t('describe.typeAnswer')}
                            className="w-full p-3 rounded-xl border border-gray-200 focus:border-orella-500 focus:ring-1 focus:ring-orella-500 outline-none resize-none overflow-hidden min-h-[80px] text-gray-700"
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        )}
      </div>

      </div>

      {/* Bottom Buttons — PROD-1194 part C: pinned on top of the TabBar (BottomActionBar aboveTabBar), so
          Next / Review My Answers stays in sight while a long question list scrolls. It sat INSIDE the
          content area above (in flow, under every question); it is now the page column's own child, and
          the "tap the alert to continue" line is the bar's note, pinned with the button. */}
      <BottomActionBar
        aboveTabBar
        className="[--bar-px:16px]"
        note={pendingAlertAck ? (
          <p role="status" className="text-center text-xs font-semibold text-red-700 mb-2">{t('describe.alertTapToContinue')}</p>
        ) : null}
        style={{ 
          transition: 'opacity 0.4s ease, transform 0.4s ease',
          transitionDelay: '150ms',
          opacity: showContent ? 1 : 0,
          transform: showContent ? 'translateY(0)' : 'translateY(8px)'
        }}
      >
        {isLocked ? (
          <div className="flex items-center gap-2.5">
            <button
              onClick={() => navigate(stepInfo.next)}
              className="flex-1 py-3 rounded-full bg-orella-500 text-white font-bold text-[15px] breathe-blue"
            >
              {t('common.next')}
            </button>
          </div>
        ) : category === 'additional' ? (
          /* Step 5 (last) — Trash + Review My Answers */
          <div className="flex items-center gap-2.5">
            <button
              onClick={() => setShowDeleteModal(true)}
              className="w-[42px] h-[42px] rounded-full border-[1.5px] border-red-100 flex items-center justify-center flex-shrink-0 active:bg-red-50 transition-colors"
            >
              <Trash2 size={16} className="text-red-400" />
            </button>
            <button
              onClick={handleNext}
              disabled={pendingAlertAck}
              className="flex-1 py-3 rounded-full bg-orella-500 text-white font-bold text-[15px] active:bg-orella-600 transition-colors breathe-blue"
            >
              {t('describe.reviewMyAnswers') || 'Review My Answers'}
            </button>
          </div>
        ) : (
          /* Steps 2–4 — Trash + Next + Skip to End */
          <div className="flex items-center gap-2.5">
            <button
              onClick={() => setShowDeleteModal(true)}
              className="w-[42px] h-[42px] rounded-full border-[1.5px] border-red-100 flex items-center justify-center flex-shrink-0 active:bg-red-50 transition-colors"
            >
              <Trash2 size={16} className="text-red-400" />
            </button>
            {(() => {
              const hasAnswers = questions.some(q => {
                const key = getAnswerKey(q.id)
                const val = answers[key]
                return val && (Array.isArray(val) ? val.length > 0 : true)
              })
              return (
                <button
                  onClick={handleNext}
              disabled={pendingAlertAck}
                  className={`flex-1 py-3 rounded-full font-bold text-[15px] transition-colors ${
                    hasAnswers 
                      ? 'bg-orella-500 text-white active:bg-orella-600 breathe-blue' 
                      : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {t('common.next') || 'Next'}
                </button>
              )
            })()}
            <button
              onClick={handleSkipToReview}
              disabled={pendingAlertAck}
              className="px-4 py-3 rounded-full border-[1.5px] border-gray-200 text-gray-400 text-[13px] font-semibold flex-shrink-0 active:bg-gray-50 transition-colors whitespace-nowrap"
            >
              {t('describe.skipToEnd') || 'Skip to end'}
            </button>
          </div>
        )}
      </BottomActionBar>

      {/* Delete Confirmation Modal */}
      <DeleteCaseModal canDelete={!isHeldByDoctor(currentCase)}
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={handleDeleteCase}
        onSaveAsDraft={handleSaveAsDraft}
        caseName={currentCase?.focusAreaName || currentCase?.focusArea?.name || ''}
      />
      </>
      )}
    </div>
  )
}
